import { Collection } from 'firestorter';

import BaseDocument from '../../Model/BaseDocument';
import Collections from '../../utils/collections';

const MeasurementUnit = {
  METRIC: 'meters/kg',
  IMPERIAL: 'pounds/feet',
};

const createdAtMillis = (doc) => doc.createdAt?.toMillis?.() ?? doc.createdAt?.getTime?.() ?? 0;
class PostPaymentFormAnswer extends BaseDocument {
  constructor(id, opts) {
    super(`${Collections.POST_PAYMENT_FORM_ANSWER}/${id}`, opts);
  }

  get unit() {
    return this.data.unit;
  }

  get activityLevel() {
    return this.data.activityLevel;
  }

  get currentWorkoutDays() {
    return this.data.currentWorkoutDays;
  }

  get daysAvailableToWorkout() {
    return this.data.daysAvailableToWorkout;
  }

  get weightsExperience() {
    return this.data.weightsExperience;
  }

  get dietRestrictions() {
    return this.data.dietRestrictions;
  }

  get biologicalSex() {
    return this.data.biologicalSex;
  }

  get answers() {
    return this.data.answers;
  }

  get createdAt() {
    return this.data.createdAt;
  }

  get submissionId() {
    return this.data.submissionId;
  }

  get birthdate() {
    return this.data.birthdate;
  }

  get currentWeight() {
    return this.unit === MeasurementUnit.METRIC
      ? this.data.currentWeightKgs
      : this.data.currentWeightLbs;
  }

  get height() {
    return this.unit === MeasurementUnit.METRIC
      ? this.data.heightCm
      : { ft: this.data.heightFt, in: this.data.heightIn };
  }

  get weeklyWeightGoal() {
    return this.unit === MeasurementUnit.METRIC
      ? this.data.weeklyWeightGoalKgs
      : this.data.weeklyWeightGoalLbs;
  }

  get user() {
    return this.data.user;
  }

  get name() {
    return this.data.name;
  }

  get email() {
    return this.data.email;
  }

  /**
   * Every submission the client has made, newest first. Sorted in JS rather than with
   * `orderBy('createdAt', 'desc')` because that needs a composite index in system2-server, for a result
   * set that is one or two docs per client. Any documents predating the `createdAt` field tie at 0, so
   * among those the first one Firestore returns wins.
   *
   * @param {string} userId The client the submissions belong to
   * @param {string} [coachId] Restrict to one coach's submissions
   * @returns {Promise<PostPaymentFormAnswer[]>} Every matching submission, newest first.
   */
  static async getAllByUser(userId, coachId) {
    const collection = new Collection(Collections.POST_PAYMENT_FORM_ANSWER, {
      createDocument: (src, opts) => new PostPaymentFormAnswer(src, opts),
      query: (ref) => {
        const byUser = ref.where('user', '==', userId);
        return coachId ? byUser.where('coach', '==', coachId) : byUser;
      },
    });
    await collection.fetch();
    return [...collection.docs].sort((a, b) => createdAtMillis(b) - createdAtMillis(a));
  }

  /**
   * The client's newest submission.
   *
   * @param {string} userId The client the submissions belong to
   * @param {string} [coachId] Restrict to one coach's submissions
   * @returns {Promise<PostPaymentFormAnswer|null>} The newest submission, or null when there are none.
   */
  static async getLatestByUser(userId, coachId) {
    const docs = await PostPaymentFormAnswer.getAllByUser(userId, coachId);
    return docs[0] ?? null;
  }
}

export default PostPaymentFormAnswer;
